import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Search, ArrowRight, ShieldCheck, FileText, Globe, Wrench } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO = "https://images.unsplash.com/photo-1496247749665-49cf5b1022e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3ODE4MjY4NjF8MA&ixlib=rb-4.1.0&q=85";
const TEXTURE = "https://images.unsplash.com/photo-1593062037896-764e9f52029e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtZXRhbCUyMGdlYXJzfGVufDB8fHx8MTc4MTgyNjg2MXww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/vendors/search?featured=true&limit=200`).then((r) => setFeatured(r.data)).catch(() => {});
    axios.get(`${API}/categories`).then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-800">
        <div className="absolute inset-0">
          <img src={HERO} alt="Industrial Factory" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/80 to-[#0A0D14]" />
        </div>
        <div className="relative z-10 px-6 sm:px-12 lg:px-24 pt-24 pb-32 sm:pt-32 sm:pb-40">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-orange-600/40 bg-orange-600/10 mb-8" data-testid="hero-tagline">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-orange-500">Industrial Vendor Network</span>
            </div>
            <h1 className="font-display font-black uppercase tracking-tighter text-slate-50 text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Built by contractors.<br />
              <span className="text-orange-500">Found by buyers.</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">
              The trusted directory for industrial vendors and contractors. Search verified suppliers, download line cards, connect directly. Free for buyers — always.
            </p>

            <form onSubmit={submit} className="mt-12 flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by vendor name, keyword, or service..."
                  className="w-full pl-12 pr-4 py-4 bg-slate-900 border border-slate-700 text-slate-50 placeholder:text-slate-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none rounded-sm font-mono text-sm"
                />
              </div>
              <button type="submit" data-testid="hero-search-button" className="px-8 py-4 bg-orange-600 text-white font-bold uppercase tracking-wider hover:bg-orange-500 transition-colors rounded-sm inline-flex items-center justify-center gap-2">
                Search <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
              {[
                { n: "100%", l: "Free Search" },
                { n: "Verified", l: "Vendor Approval" },
                { n: "PDF", l: "Line Cards" },
                { n: "Direct", l: "Contact Info" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-orange-600 pl-4">
                  <div className="font-display font-black text-2xl text-slate-50">{s.n}</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="px-6 sm:px-12 lg:px-24 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">// Trade Categories</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-50">Browse by trade</h2>
          </div>
          <Link to="/search" data-testid="browse-all-link" className="hidden sm:flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-400 hover:text-orange-500">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-slate-800">
          {categories.slice(0, 12).map((c) => (
            <Link
              key={c.id}
              to={`/search?category=${encodeURIComponent(c.name)}`}
              data-testid={`category-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="bg-slate-900 hover:bg-slate-800 p-6 transition-colors group"
            >
              <Wrench className="w-5 h-5 text-orange-500 mb-3" />
              <div className="font-display font-bold text-slate-100 text-base group-hover:text-orange-500">{c.name}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 sm:px-12 lg:px-24 py-24 border-t border-slate-800">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-4">// For Vendors</div>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl text-slate-50 leading-[0.95]">
              Get found.<br />Get more work.
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed text-base">
              Join the alliance for a flat monthly fee. Upload your logo, line card, and service keywords. Get discovered by buyers searching your trade — without bid platforms or lead-resellers in the middle.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/register" data-testid="cta-register" className="px-8 py-4 bg-orange-600 text-white font-bold uppercase tracking-wider hover:bg-orange-500 rounded-sm inline-flex items-center gap-2">
                List Your Business <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing" data-testid="cta-pricing" className="px-8 py-4 border border-slate-700 text-slate-200 font-bold uppercase tracking-wider hover:border-orange-500 hover:text-orange-500 rounded-sm">
                See Pricing
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-px bg-slate-800">
            {[
              { i: ShieldCheck, t: "Verified Profile", d: "Admin-approved listings build trust." },
              { i: FileText, t: "Line Card PDF", d: "Upload and host your capabilities sheet." },
              { i: Globe, t: "Direct Website", d: "Drive traffic straight to your site." },
              { i: Search, t: "Keyword Search", d: "Be findable on dozens of trade terms." },
            ].map((b) => (
              <div key={b.t} className="bg-slate-900 p-8">
                <b.i className="w-6 h-6 text-orange-500 mb-4" />
                <div className="font-display font-bold text-slate-100 mb-2">{b.t}</div>
                <div className="text-sm text-slate-500">{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section data-testid="featured-vendors-section" className="px-6 sm:px-12 lg:px-24 py-24 border-t border-slate-800 relative">
          <div className="absolute inset-0 opacity-5">
            <img src={TEXTURE} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="relative z-10">
            <div className="mb-12">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">// Featured Vendors</div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-50">Trusted alliance members</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((v) => (
                <VendorCard key={v.id} v={v} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export function VendorCard({ v }) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  return (
    <Link
      to={`/vendor/${v.id}`}
      data-testid={`vendor-card-${v.id}`}
      className="block bg-slate-900 border border-slate-800 hover:border-orange-600 transition-colors p-6 group"
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="w-14 h-14 bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden rounded-sm flex-shrink-0">
          {v.logo_path ? (
            <img src={`${API_BASE}/api/files/${v.logo_path}`} alt={v.company_name} className="w-full h-full object-cover" />
          ) : (
            <Wrench className="w-6 h-6 text-slate-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-slate-100 text-lg leading-tight group-hover:text-orange-500 transition-colors truncate">
            {v.company_name}
          </h3>
          {v.city && <div className="text-xs text-slate-500 font-mono uppercase tracking-wider mt-1">{v.city}{v.state ? `, ${v.state}` : ""}</div>}
        </div>
        {v.featured && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-orange-600 text-white">Featured</span>
        )}
      </div>
      {v.description && <p className="text-sm text-slate-400 line-clamp-2 mb-4">{v.description}</p>}
      <div className="flex flex-wrap gap-1.5">
        {(v.categories || []).slice(0, 3).map((c) => (
          <span key={c} className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-slate-800 text-slate-300 border border-slate-700">
            {c}
          </span>
        ))}
      </div>
    </Link>
  );
}
